import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { Card, Btn } from "../components/UI";
import {
  Sparkles, Send, Loader2, ChevronLeft, SlidersHorizontal, Target, MapPin, Tv,
  Wallet, CalendarRange, Users, ShieldCheck, Activity, ChevronDown, ChevronUp, Megaphone, Rocket, Palette,
} from "lucide-react";
import { toast } from "sonner";
import { CreativeDraftsCard, LaunchChecklist, FundraiserCard, VoterFileCard } from "./agent/cards";

const SUGGESTIONS = [
  "Create a $50,000 CTV campaign for my race for the next 30 days.",
  "Reach voters in our district who care about property taxes and education.",
  "Create a campaign to introduce our candidate across the district.",
  "Build a fundraiser with a $25,000 goal and promote it with $20,000 in ads.",
  "We have $100,000 and 45 days before election day. Recommend a media plan.",
  "Use our voter file and reach those voters on CTV and online video.",
];

const usd = (v) => (v == null ? "—" : `$${Number(v).toLocaleString()}`);
const num = (v) => (v == null ? "—" : Number(v).toLocaleString());

const renderMsg = (text) => {
  const html = String(text || "")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/^#{1,4} (.*)$/gm, "<strong>$1</strong>")
    .replace(/^[-•] /gm, "• ");
  return <div dangerouslySetInnerHTML={{ __html: html }} />;
};

const PlanRow = ({ icon: Icon, label, value, accent, testid }) => (
  <div className="flex items-start gap-2.5 py-2 border-b border-white/5 last:border-0" data-testid={testid}>
    <Icon className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${accent || "text-cyan-300"}`} />
    <div className="min-w-0 flex-1">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-xs text-slate-200 mt-0.5 break-words">{value || <span className="text-slate-600">Pending</span>}</div>
    </div>
  </div>
);

export default function CampaignAgent() {
  const { sid } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [plan, setPlan] = useState({});
  const [activity, setActivity] = useState([]);
  const [campaignId, setCampaignId] = useState(null);
  const [launched, setLaunched] = useState(false);
  const [input, setInput] = useState(location.state?.prefill || "");
  const [busy, setBusy] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [launchResult, setLaunchResult] = useState(null);
  const [uploadingVoterFile, setUploadingVoterFile] = useState(false);
  const [showActivity, setShowActivity] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    api.get(`/agent/sessions/${sid}`).then((r) => {
      setSession(r.data);
      setMessages(r.data.messages || []);
      setPlan(r.data.plan || {});
      setActivity(r.data.activity || []);
      setCampaignId(r.data.campaign_id);
      setLaunched(r.data.status === "launched");
    }).catch(() => {
      toast.error("Agent session not found");
      navigate("/campaigns/new");
    });
  }, [sid, navigate]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, busy]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: msg, ts: new Date().toISOString() }]);
    try {
      const r = await api.post(`/agent/sessions/${sid}/message`, { text: msg });
      setMessages((m) => [...m, { role: "agent", content: r.data.reply, ts: new Date().toISOString() }]);
      setPlan(r.data.plan || {});
      setActivity((a) => [...a, ...(r.data.activity || [])]);
      if (r.data.campaign_id) setCampaignId(r.data.campaign_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "The agent hit a snag — try again");
      setMessages((m) => [...m, { role: "agent", content: "Sorry — I hit a snag processing that. Could you try again?", ts: new Date().toISOString() }]);
    } finally {
      setBusy(false);
    }
  };

  const draftCreatives = async () => {
    setDrafting(true);
    try {
      const { data } = await api.post(`/agent/sessions/${sid}/draft-creatives`);
      setPlan(data.plan || {});
      setActivity((a) => [...a, { ts: new Date().toISOString(), text: "Drafted ad creative — display, CTV & audio" }]);
      toast.success("Creative drafts ready — review them in the panel");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Creative drafting failed");
    } finally { setDrafting(false); }
  };

  const uploadVoterFile = async (name, csvText) => {
    setUploadingVoterFile(true);
    try {
      const { data } = await api.post(`/agent/sessions/${sid}/voter-file`, { name, csv_text: csvText });
      setPlan(data.plan || {});
      setActivity((a) => [...a, { ts: new Date().toISOString(), text: `Voter file "${name}" hashed & ${data.activated ? "activated" : "uploaded"}` }]);
      toast.success(data.activated ? "Voter file activated as a private audience" : "Voter file uploaded");
      send(`I uploaded my voter file "${name}" — ${Number(data.file.records).toLocaleString()} hashed identifiers${data.activated ? ", now activated as a private audience. Use it in the plan." : `. Note: ${data.note || ""}`}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Voter file upload failed");
    } finally { setUploadingVoterFile(false); }
  };

  const launch = async () => {
    if (!window.confirm("Approve and launch this campaign? It will go live on the ad platform.")) return;
    setLaunching(true);
    setLaunchResult(null);
    try {
      const { data } = await api.post(`/agent/sessions/${sid}/launch`, {});
      setLaunchResult(data);
      if (data.campaign_id) setCampaignId(data.campaign_id);
      if (data.success) {
        setLaunched(true);
        setActivity((a) => [...a, ...(data.activity || [])]);
        toast.success("Campaign launched 🚀");
      } else {
        toast.error("Launch stopped — see the checklist for what needs attention");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Launch failed");
    } finally { setLaunching(false); }
  };

  const channels = plan.channels || {};
  const channelText = Object.keys(channels).length
    ? Object.entries(channels).map(([c, p]) => `${c} ${p}%`).join(" · ")
    : null;
  const forecast = plan.forecast || {};
  const compliance = plan.compliance || {};
  const audiences = plan.audiences || [];
  const ready = !!(plan.objective && plan.budget && Object.keys(channels).length && plan.start_date && plan.end_date);

  if (!session) return <div className="p-8 text-slate-400 text-sm">Loading Campaign Agent…</div>;

  return (
    <div className="p-4 lg:p-6 max-w-7xl mx-auto h-[calc(100vh-1rem)] flex flex-col" data-testid="campaign-agent-page">
      <div className="flex items-center justify-between mb-3 shrink-0">
        <div>
          <button onClick={() => navigate("/campaigns/new")} className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-cyan-300" data-testid="back-to-choice">
            <ChevronLeft className="w-3.5 h-3.5" /> Create Campaign
          </button>
          <h1 className="font-display text-xl lg:text-2xl font-bold tracking-tight flex items-center gap-2 mt-0.5">
            <Sparkles className="w-5 h-5 text-cyan-300" /> TotalReach Campaign Agent
          </h1>
        </div>
        {campaignId && (
          <Btn variant="secondary" onClick={() => navigate(`/campaigns/${campaignId}`)} data-testid="open-pro-mode-link">
            <SlidersHorizontal className="w-4 h-4" /> Open Pro Mode
          </Btn>
        )}
      </div>

      <div className="flex-1 grid lg:grid-cols-3 gap-4 min-h-0">
        {/* Chat */}
        <Card className="lg:col-span-2 flex flex-col min-h-0 !p-0" padded={false}>
          <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="agent-chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${
                  m.role === "user"
                    ? "bg-cyan-400/15 border border-cyan-400/20 text-cyan-50"
                    : "bg-white/[0.04] border border-white/10 text-slate-200"
                }`} data-testid={`agent-msg-${m.role}-${i}`}>
                  {renderMsg(m.content)}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div className="rounded-2xl px-4 py-2.5 text-xs bg-white/[0.04] border border-white/10 text-slate-400 inline-flex items-center gap-2" data-testid="agent-thinking">
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-cyan-300" /> TotalReach is working — checking audiences, media and pricing…
                </div>
              </div>
            )}
            {messages.length <= 1 && !busy && (
              <div className="pt-2">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Try one of these</div>
                <div className="flex flex-wrap gap-2">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} onClick={() => send(s)} data-testid="agent-suggestion"
                      className="text-[11px] px-3 py-1.5 rounded-full border border-white/10 text-slate-400 hover:border-cyan-400/30 hover:text-cyan-300 transition-colors text-left">
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>
          <div className="p-3 border-t border-white/10 shrink-0">
            <div className="flex gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                rows={2}
                placeholder="Tell TotalReach what you want your campaign to do…"
                data-testid="agent-input"
                className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm resize-none focus:outline-none focus:border-cyan-400/50 placeholder:text-slate-600"
              />
              <Btn onClick={() => send()} disabled={busy || !input.trim()} data-testid="agent-send-btn" className="self-end">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Btn>
            </div>
          </div>
        </Card>

        {/* Live plan */}
        <div className="flex flex-col gap-3 min-h-0 overflow-y-auto">
          <Card data-testid="live-campaign-plan" className="!p-4">
            <div className="flex items-center justify-between mb-1">
              <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Campaign Plan</div>
              <span className={`text-[10px] px-2 py-0.5 rounded-full border ${launched ? "border-cyan-400/40 bg-cyan-400/10 text-cyan-300" : ready ? "border-emerald-400/30 bg-emerald-400/10 text-emerald-300" : "border-amber-400/30 bg-amber-400/10 text-amber-300"}`} data-testid="plan-status-pill">
                {launched ? "Launched" : campaignId ? "Draft created" : ready ? "Ready" : "Building"}
              </span>
            </div>
            <PlanRow icon={Target} label="Goal" value={plan.objective} testid="plan-goal" />
            <PlanRow icon={Users} label="Audience"
              value={audiences.length ? `${audiences.map((a) => a.name).join(", ")}${plan.audience_reach ? ` — ${num(plan.audience_reach)} est. reach` : ""}` : plan.audience_query}
              testid="plan-audience" />
            <PlanRow icon={MapPin} label="Geography" value={plan.geography} testid="plan-geography" />
            <PlanRow icon={Tv} label="Channels" value={channelText} accent={channels["Connected TV"] ? "text-emerald-300" : undefined} testid="plan-channels" />
            <PlanRow icon={Wallet} label="Budget" value={plan.budget ? usd(plan.budget) : null} testid="plan-budget" />
            <PlanRow icon={CalendarRange} label="Duration"
              value={plan.start_date ? `${plan.start_date} → ${plan.end_date}${plan.duration_days ? ` (${plan.duration_days} days)` : ""}` : null}
              testid="plan-duration" />
            <PlanRow icon={Activity} label="Forecast"
              value={forecast.impressions ? `${num(forecast.impressions)} impressions · ${num(forecast.estimated_reach)} reach · ${forecast.estimated_frequency}x freq — ${forecast.label}` : null}
              testid="plan-forecast" />
            <PlanRow icon={ShieldCheck} label="Compliance"
              value={compliance.items ? (compliance.ready ? "Ready" : `${compliance.items.length} item(s) pending`) : null}
              accent={compliance.ready ? "text-emerald-300" : "text-amber-300"} testid="plan-compliance" />
          </Card>

          {channels["Connected TV"] && (
            <div className="rounded-lg border border-emerald-400/25 bg-emerald-400/5 px-3.5 py-2.5 text-xs text-emerald-200 flex items-center gap-2" data-testid="ctv-hero-badge">
              <Tv className="w-4 h-4" /> Recommended: CTV + Digital — television-quality messaging extended across every screen.
            </div>
          )}

          {campaignId && !launched && (
            <Card className="!p-4 border-cyan-400/25" data-testid="campaign-ready-card">
              <div className="font-display font-semibold text-sm flex items-center gap-2"><Megaphone className="w-4 h-4 text-cyan-300" /> Your campaign draft is ready</div>
              <p className="text-[11px] text-slate-500 mt-1">Draft creative, review the plan, then approve the launch — you stay in control.</p>
              <div className="flex flex-col gap-2 mt-3">
                {!plan.creative && (
                  <Btn variant="secondary" onClick={draftCreatives} disabled={drafting} data-testid="draft-creatives-btn">
                    {drafting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Palette className="w-4 h-4" />}
                    {drafting ? "Drafting creative…" : "Draft creative (display · CTV · audio)"}
                  </Btn>
                )}
                <Btn onClick={launch} disabled={!ready || launching} data-testid="approve-launch-btn">
                  {launching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
                  {launching ? "Launching…" : "Approve & Launch"}
                </Btn>
                <Btn variant="secondary" onClick={() => navigate(`/campaigns/${campaignId}`)} data-testid="ready-open-pro">
                  <SlidersHorizontal className="w-4 h-4" /> Review in Pro Mode
                </Btn>
              </div>
            </Card>
          )}

          {launched && !launchResult && (
            <Card className="!p-4 border-emerald-400/30" data-testid="launched-card">
              <div className="font-display font-semibold text-sm flex items-center gap-2 text-emerald-200"><Rocket className="w-4 h-4 text-emerald-300" /> Campaign launched</div>
              <p className="text-[11px] text-slate-500 mt-1">This campaign is live on the ad platform. Track delivery in Reports.</p>
              <Btn variant="secondary" className="mt-3 w-full" onClick={() => navigate(`/campaigns/${campaignId}`)}>Open campaign</Btn>
            </Card>
          )}

          {launchResult && (
            <Card className={`!p-4 ${launchResult.success ? "border-emerald-400/30" : "border-rose-400/30"}`} data-testid="launch-result-card">
              <div className="font-display font-semibold text-sm flex items-center gap-2">
                <Rocket className={`w-4 h-4 ${launchResult.success ? "text-emerald-300" : "text-rose-300"}`} />
                {launchResult.success ? "Launch complete" : "Launch needs attention"}
              </div>
              <LaunchChecklist steps={launchResult.steps || []} />
              {launchResult.success && (
                <Btn variant="secondary" className="mt-3 w-full" onClick={() => navigate(`/campaigns/${campaignId}`)} data-testid="launch-open-campaign">
                  Open campaign
                </Btn>
              )}
            </Card>
          )}

          <VoterFileCard wizard={plan.voter_file_wizard} voterFile={plan.voter_file} onUpload={uploadVoterFile} uploading={uploadingVoterFile} />
          <CreativeDraftsCard creative={plan.creative} />
          <FundraiserCard fundraiser={plan.fundraiser} />

          <Card className="!p-4" data-testid="agent-activity-panel">
            <button onClick={() => setShowActivity(!showActivity)} className="w-full flex items-center justify-between text-xs uppercase tracking-wider text-slate-400 font-semibold" data-testid="toggle-activity">
              <span className="flex items-center gap-2"><Activity className="w-3.5 h-3.5 text-cyan-300" /> What TotalReach did ({activity.length})</span>
              {showActivity ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
            {showActivity && (
              <div className="mt-3 space-y-1.5 max-h-44 overflow-y-auto">
                {activity.map((a, i) => (
                  <div key={i} className="text-[11px] text-slate-400 flex items-start gap-1.5">
                    <span className="text-emerald-300 mt-px">✓</span> {a.text}
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
